/**
 * ExtractionReviewModal — FA pack, FA5.
 *
 * Shown after the user uploads a financial model. The FA2 extractor
 * returns an `ExtractionResult` with confidence-tiered hits; this
 * modal lets the user:
 *   - Bulk-accept the high-confidence fields.
 *   - Confirm / Edit / Reject each medium-confidence field.
 *   - Enter manual values for missing fields.
 *
 * Commit gathers the accepted values + fills in source metadata and
 * calls `onCommit(next)` with the full FinancialAssumptions. The host
 * is responsible for calling `updateSiteFinancialAssumptions`.
 *
 * UX discipline:
 *   - Three buckets visually separated: High (auto-accept) / Medium
 *     (per-row confirm) / Missing (manual entry inline).
 *   - Warnings strip at the top renders the extractor's warnings
 *     (e.g. "Found 2 cells labelled LCOE — picked Inputs!B14").
 *   - Discard upload returns control to the caller without committing.
 *   - Commit button is disabled until the user has at least
 *     reviewed (clicked accept-all-medium, OR every medium row has a
 *     decision).
 */
import React, { useCallback, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, CircleSlash, Edit3 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import type {
  CurrencyCode,
  FieldProvenance,
  FinancialAssumptions,
  FinancialAssumptionsCore,
  FinancialField,
} from '../../../types/financialAssumptions';
import {
  applyCampusPatch,
  emptyFinancialAssumptions,
  PHASE_DERIVED_CAMPUS_FIELDS,
} from '../../../types/financialAssumptions';
import type {
  ExtractionResult,
  ExtractionReviewFlag,
} from '../../../services/financial/extractFinancialModel';
import { hasMonetaryValues } from '../../../services/financial/extractFinancialModel';

export interface ExtractionReviewModalProps {
  /** Controls visibility. Host should set false to dismiss. */
  open: boolean;
  /** ExtractionResult from `extractFinancialModel`. */
  extraction: ExtractionResult;
  /** Source file name for the modal header + source metadata. */
  sourceFileName?: string;
  /** Source file SHA-256 (pins reports to this model version). */
  sourceFileHash?: string;
  /** Pre-existing site assumptions, merged with the accepted values. */
  baseAssumptions: FinancialAssumptions | null;
  /** Identifier of the editor (uid / email) — stamped on the FA. */
  updatedBy: string;
  /** Called with the final FA when the user clicks Commit. */
  onCommit: (next: FinancialAssumptions) => void | Promise<void>;
  /** Called when the user clicks Discard. */
  onDiscard: () => void;
}

type MediumDecision = 'pending' | 'confirmed' | 'rejected';

export function ExtractionReviewModal(props: ExtractionReviewModalProps): React.JSX.Element {
  const {
    open,
    extraction,
    sourceFileName,
    sourceFileHash,
    baseAssumptions,
    updatedBy,
    onCommit,
    onDiscard,
  } = props;

  // Cautions the extractor refused to resolve on its own (F3 unit-scale
  // implausibility, A8 debt:equity ambiguity). A blocking flag pulls its
  // fields OUT of the auto-accepted high bucket — a caution nobody has to
  // click past is a caution nobody reads.
  const flagsByField = useMemo(() => groupFlagsByField(extraction.reviewFlags ?? []), [extraction.reviewFlags]);
  const blockingFields = useMemo(() => {
    const set = new Set<FinancialField>();
    for (const flag of extraction.reviewFlags ?? []) {
      if (!flag.blocksBulkAccept) continue;
      for (const field of flag.fields) set.add(field);
    }
    return set;
  }, [extraction.reviewFlags]);

  // Partition the extracted fields by tier.
  const partitioned = useMemo(
    () => partitionByTier(extraction, blockingFields),
    [extraction, blockingFields],
  );

  // Editable values for medium-confidence rows. Allows the user to
  // tweak before confirming.
  const [mediumValues, setMediumValues] = useState<Record<string, unknown>>(() => {
    const seed: Record<string, unknown> = {};
    for (const [field, value] of Object.entries(partitioned.medium)) {
      seed[field] = value;
    }
    return seed;
  });

  // Decision state — pending / confirmed / rejected per medium field.
  const [mediumDecisions, setMediumDecisions] = useState<Record<string, MediumDecision>>(() => {
    const seed: Record<string, MediumDecision> = {};
    for (const field of Object.keys(partitioned.medium)) {
      seed[field] = 'pending';
    }
    return seed;
  });

  // Manual entry values for missing fields.
  const [missingValues, setMissingValues] = useState<Record<string, string>>({});

  const [error, setError] = useState<string | null>(null);
  const [committing, setCommitting] = useState(false);

  // ── Currency (A10) ────────────────────────────────────────────────
  // The engine computes in USD. We detect the workbook's currency where
  // the sheet declares one, make the user confirm it, and REQUIRE a rate
  // when it isn't USD. Nothing is converted here — see the note below.
  const monetaryExtracted = useMemo(() => hasMonetaryValues(extraction.assumptions), [extraction.assumptions]);
  // Raw text so the field can be cleared and retyped; an empty box means
  // "USD" (what every writer assumed before currency was read at all).
  const [currencyInput, setCurrencyInput] = useState<string>(
    () => String(extraction.detectedCurrency?.code ?? baseAssumptions?.currency ?? 'USD'),
  );
  const currencyCode: CurrencyCode = currencyInput.trim().toUpperCase() || 'USD';
  const [exchangeRate, setExchangeRate] = useState<string>(
    () => (baseAssumptions?.exchangeRateToUsd != null ? String(baseAssumptions.exchangeRateToUsd) : ''),
  );
  const needsExchangeRate = monetaryExtracted && currencyCode !== 'USD';
  const parsedExchangeRate = Number(exchangeRate.replace(/[,\s]/g, ''));
  const exchangeRateValid = Number.isFinite(parsedExchangeRate) && parsedExchangeRate > 0;

  const setMediumDecision = useCallback((field: string, decision: MediumDecision) => {
    setMediumDecisions(prev => ({ ...prev, [field]: decision }));
  }, []);

  const setMediumValue = useCallback((field: string, raw: string) => {
    const parsed = Number(raw.replace(/[$,\s%]/g, ''));
    setMediumValues(prev => ({
      ...prev,
      [field]: Number.isFinite(parsed) ? parsed : raw,
    }));
  }, []);

  // "Accept all" deliberately SKIPS flagged rows: the extractor could not
  // decide those, so a bulk click must not decide them either.
  const acceptAllMedium = useCallback(() => {
    setMediumDecisions(prev => {
      const next = { ...prev };
      for (const k of Object.keys(next)) {
        if (blockingFields.has(k as FinancialField)) continue;
        if (next[k] === 'pending') next[k] = 'confirmed';
      }
      return next;
    });
  }, [blockingFields]);

  const allMediumReviewed = useMemo(
    () => Object.values(mediumDecisions).every(d => d !== 'pending'),
    [mediumDecisions],
  );

  const commitBlockedReason = useMemo(() => {
    if (!allMediumReviewed) return 'Review every medium-confidence row first';
    if (needsExchangeRate && !exchangeRateValid) {
      return `Enter the ${currencyCode} → USD exchange rate before committing`;
    }
    return null;
  }, [allMediumReviewed, currencyCode, exchangeRateValid, needsExchangeRate]);

  const handleCommit = useCallback(async () => {
    setError(null);
    if (needsExchangeRate && !exchangeRateValid) {
      setError(`Enter the ${currencyCode} → USD exchange rate before committing.`);
      return;
    }
    setCommitting(true);
    try {
      const now = new Date().toISOString();
      const base = baseAssumptions ?? emptyFinancialAssumptions(now, updatedBy);
      const merged: FinancialAssumptions = {
        ...base,
        source: baseAssumptions ? 'hybrid' : 'uploaded-model',
        sourceFileName: sourceFileName ?? base.sourceFileName,
        sourceFileHash: sourceFileHash ?? base.sourceFileHash,
        // The declared currency of the numbers being written. Nothing
        // converts them yet — the rate is recorded so the boundary
        // normalisation can, and so the record never claims USD it isn't.
        currency: monetaryExtracted ? currencyCode : base.currency,
        ...(needsExchangeRate && exchangeRateValid
          ? { exchangeRateToUsd: parsedExchangeRate }
          : {}),
        updatedAt: now,
        updatedBy,
        fieldProvenance: { ...base.fieldProvenance },
      };

      // Track WHICH fields this commit actually writes, so the phase-aware
      // patch below only moves values the user reviewed (never the whole core).
      const written = new Set<FinancialField>();

      // Apply high-confidence values verbatim.
      for (const [field, value] of Object.entries(partitioned.high)) {
        applyFieldOnto(merged, field as FinancialField, value);
        written.add(field as FinancialField);
        const fp = extraction.fieldProvenance[field as FinancialField];
        if (fp) merged.fieldProvenance[field as FinancialField] = fp;
      }

      // Apply confirmed medium-confidence values (with user edits if
      // any). Rejected ones are dropped entirely.
      for (const [field, decision] of Object.entries(mediumDecisions)) {
        if (decision !== 'confirmed') continue;
        const value = mediumValues[field];
        applyFieldOnto(merged, field as FinancialField, value);
        written.add(field as FinancialField);
        const fp = extraction.fieldProvenance[field as FinancialField];
        if (fp) {
          // Bump confidence to `high` since the user just confirmed.
          merged.fieldProvenance[field as FinancialField] = { ...fp, confidence: 'high', extractedAt: now };
        }
      }

      // Apply manual-entry values for missing fields.
      for (const [field, raw] of Object.entries(missingValues)) {
        if (raw.trim() === '') continue;
        const parsed = Number(raw.replace(/[$,\s%]/g, ''));
        if (!Number.isFinite(parsed)) continue;
        applyFieldOnto(merged, field as FinancialField, parsed);
        written.add(field as FinancialField);
        const manual: FieldProvenance = { source: 'manual', confidence: 'high', extractedAt: now };
        merged.fieldProvenance[field as FinancialField] = manual;
      }

      // ── PHASE-AWARE COMMIT ──────────────────────────────────────────────
      // `phases[]` is authoritative on write: the save path re-derives campus
      // capacity / CAPEX / CF / COD from the phases. Writing an extracted value
      // onto the CORE alone therefore looked like it worked (the review modal
      // and the form both showed it) and was then silently overwritten by the
      // next save — while its `extracted-*` provenance chip survived, so the
      // user saw a stamped, sourced number the record did not contain.
      // Route the rollup-derived fields onto the phase instead.
      const phasePatch: Partial<FinancialAssumptionsCore> = {};
      for (const field of PHASE_DERIVED_CAMPUS_FIELDS) {
        if (written.has(field)) {
          (phasePatch as Record<string, unknown>)[field] = merged[field];
        }
      }
      const { next, unapplied } = applyCampusPatch(merged, phasePatch);
      if (unapplied.length > 0) {
        // A multi-phase model cannot attribute a single campus figure to a
        // phase. Say so rather than dropping the value on the floor.
        setError(
          `Saved, but ${unapplied.join(', ')} could not be applied: this model has `
          + `${(merged.phases ?? []).length} phases, so a single campus-level figure cannot be attributed `
          + `to one of them. Enter ${unapplied.length === 1 ? 'it' : 'them'} on the phase you mean.`,
        );
      }

      await onCommit(next);
    } catch (e) {
      setError((e as Error)?.message ?? 'Commit failed');
    } finally {
      setCommitting(false);
    }
  }, [
    baseAssumptions, currencyCode, exchangeRateValid, extraction, mediumDecisions,
    mediumValues, missingValues, monetaryExtracted, needsExchangeRate, onCommit,
    parsedExchangeRate, partitioned.high, sourceFileHash, sourceFileName, updatedBy,
  ]);

  // The list of canonical fields the extractor knows about that came
  // back missing. Show only a small set in the modal (the four output
  // metrics + a few common inputs) to keep the UI from sprawling.
  const missingFields = useMemo<FinancialField[]>(() => {
    const PRIORITY: FinancialField[] = [
      'capacityMW', 'capacityFactorPct', 'projectLifeYears',
      'capexTotalUsd', 'fixedOmPerKwYearUsd', 'fuelCostPerMwhUsd',
      'waccPct', 'ppaPricePerMwhUsd',
      'lcoeNominalPerMwhUsd', 'projectIrrPct', 'npvUsd', 'paybackYears',
    ];
    return PRIORITY.filter(
      f =>
        extraction.assumptions[f] === undefined
        && !(f in mediumDecisions)
        && !(f in partitioned.high),
    );
  }, [extraction.assumptions, mediumDecisions, partitioned.high]);

  return (
    <Dialog open={open} onOpenChange={openNow => { if (!openNow) onDiscard(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Extraction complete — review and commit</DialogTitle>
          <DialogDescription>
            {sourceFileName ? `Source: ${sourceFileName}` : 'Source: uploaded model'}
            {sourceFileHash ? ` · pinned sha256 ${sourceFileHash.slice(0, 12)}…` : ''}
            {` · ${extraction.summary.highConfidence} high-confidence · ${extraction.summary.mediumConfidence} medium · ${extraction.summary.lowConfidence} low · ${extraction.summary.missing} missing`}
          </DialogDescription>
        </DialogHeader>

        {/* Cautions the extractor refused to resolve. Not warnings — these
            name fields and demand a decision. */}
        {(extraction.reviewFlags ?? []).length > 0 && (
          <div
            data-testid="extraction-review-cautions"
            className="rounded-md border border-danger/50 bg-danger/5 px-3 py-2 text-[11px] text-text-secondary"
          >
            <div className="flex items-center gap-1.5 text-danger font-semibold mb-1">
              <AlertTriangle className="w-3.5 h-3.5" />
              {extraction.reviewFlags.length} value{extraction.reviewFlags.length === 1 ? '' : 's'} need your decision
            </div>
            <ul className="list-disc list-inside space-y-1">
              {extraction.reviewFlags.map((flag, i) => (
                <li key={`${flag.code}-${i}`}>
                  <span className="font-semibold text-text-primary">
                    {flag.fields.map(f => prettyFieldName(f)).join(', ')}
                  </span>
                  {' — '}
                  {flag.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Currency (A10). The engine computes in USD; a non-USD workbook
            cannot be committed without the user declaring the rate. */}
        {monetaryExtracted && (
          <div
            data-testid="extraction-currency-block"
            className="rounded-md border border-border bg-surface-2 px-3 py-2 text-[11px] text-text-secondary space-y-2"
          >
            <div className="type-section">
              Currency of this model
            </div>
            <div>
              {extraction.detectedCurrency && extraction.detectedCurrency.code !== 'USD' ? (
                <>
                  This workbook looks like it is denominated in{' '}
                  <span className="font-semibold text-text-primary">{extraction.detectedCurrency.code}</span>
                  {' — read from '}
                  <span className="font-mono">“{extraction.detectedCurrency.evidence}”</span>
                  {extraction.detectedCurrency.cellReference ? ` at ${extraction.detectedCurrency.cellReference}` : ''}
                  . Confirm or correct it.
                </>
              ) : (
                <>Confirm the currency the extracted figures are stated in.</>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="inline-flex items-center gap-1.5">
                <span className="text-text-muted">Currency</span>
                <Input
                  type="text"
                  aria-label="Workbook currency"
                  value={currencyInput}
                  onChange={e => setCurrencyInput(e.target.value)}
                  className="w-24 uppercase"
                />
              </label>
              {needsExchangeRate && (
                <label className="inline-flex items-center gap-1.5">
                  <span className="text-text-muted">1 {currencyCode} =</span>
                  <Input
                    type="text"
                    aria-label={`Exchange rate from ${currencyCode} to USD`}
                    placeholder="e.g. 1.08"
                    value={exchangeRate}
                    onChange={e => setExchangeRate(e.target.value)}
                    className="w-28"
                  />
                  <span className="text-text-muted">USD</span>
                </label>
              )}
            </div>
            <div className="text-text-muted">
              The project-finance engine computes in USD.
              {needsExchangeRate
                ? ' The rate is recorded on the model — figures are NOT converted yet, so LCOE, NPV and IRR will read in '
                  + `${currencyCode} until currency normalisation is wired at the engine boundary.`
                : ' Figures are taken as USD.'}
            </div>
          </div>
        )}

        {extraction.warnings.length > 0 && (
          <div className="rounded-md border border-warning/50 bg-warning/5 px-3 py-2 text-[11px] text-text-secondary">
            <div className="flex items-center gap-1.5 text-warning font-semibold mb-1">
              <AlertTriangle className="w-3.5 h-3.5" />
              {extraction.warnings.length} extractor warning{extraction.warnings.length === 1 ? '' : 's'}
            </div>
            <ul className="list-disc list-inside space-y-0.5">
              {extraction.warnings.slice(0, 5).map((w, i) => <li key={i}>{w}</li>)}
              {extraction.warnings.length > 5 && <li className="text-text-muted">… and {extraction.warnings.length - 5} more</li>}
            </ul>
          </div>
        )}

        {/* High-confidence bucket — auto-accepted, just a summary. */}
        <section className="border border-border rounded-md overflow-hidden">
          <header className="px-3 py-2 border-b border-border bg-state-hover/40">
            <span className="text-[11px] font-semibold uppercase tracking-widest text-success">
              High confidence — {Object.keys(partitioned.high).length} fields
            </span>
          </header>
          <ul className="divide-y divide-border">
            {Object.entries(partitioned.high).map(([field, value]) => (
              <li key={field} className="grid grid-cols-[220px_1fr_auto] gap-3 px-3 py-1.5 items-center">
                <span className="text-[11px] text-text-secondary">{prettyFieldName(field as FinancialField)}</span>
                <span className="text-[11px] text-text-primary font-mono">{prettyFormatValue(field as FinancialField, value)}</span>
                <span className="type-body uppercase tracking-widest text-success inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> {extraction.fieldProvenance[field as FinancialField]?.cellReference ?? ''}
                </span>
              </li>
            ))}
            {Object.keys(partitioned.high).length === 0 && (
              <li className="px-3 py-2 text-[11px] text-text-muted">No high-confidence extractions.</li>
            )}
          </ul>
        </section>

        {/* Medium-confidence bucket — per-row review. */}
        <section className="border border-border rounded-md overflow-hidden">
          <header className="px-3 py-2 border-b border-border bg-state-hover/40 flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-widest text-warning">
              Medium confidence — verify each
            </span>
            {Object.keys(partitioned.medium).length > 0 && (
              <Button variant="outline" size="sm" onClick={acceptAllMedium}>
                Accept all
              </Button>
            )}
          </header>
          <ul className="divide-y divide-border">
            {Object.entries(partitioned.medium).map(([field, _value]) => {
              const rowFlags = flagsByField.get(field as FinancialField) ?? [];
              return (
                <li key={field} className="px-3 py-2 space-y-2">
                  <div className="grid grid-cols-[180px_1fr_auto] gap-3 items-center">
                    <div>
                      <div className="text-[11px] text-text-secondary">{prettyFieldName(field as FinancialField)}</div>
                      <div className="type-body uppercase tracking-widest text-text-muted">
                        {extraction.fieldProvenance[field as FinancialField]?.cellReference ?? ''}
                      </div>
                    </div>
                    <Input
                      type="text"
                      value={String(mediumValues[field] ?? '')}
                      onChange={e => setMediumValue(field, e.target.value)}
                      className={mediumDecisions[field] === 'rejected' ? 'opacity-50 line-through' : undefined}
                    />
                    <div className="flex gap-1">
                      <Button
                        variant={mediumDecisions[field] === 'confirmed' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setMediumDecision(field, 'confirmed')}
                      >
                        <CheckCircle2 className="w-3 h-3" />
                      </Button>
                      <Button
                        variant={mediumDecisions[field] === 'rejected' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setMediumDecision(field, 'rejected')}
                      >
                        <CircleSlash className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  {rowFlags.map((flag, i) => (
                    <div
                      key={`${flag.code}-${i}`}
                      data-testid={`extraction-caution-${field}`}
                      className="rounded-md border border-danger/40 bg-danger/5 px-2.5 py-2 text-[11px] text-text-secondary"
                    >
                      <div className="flex items-start gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 text-danger shrink-0 mt-0.5" />
                        <span>{flag.message}</span>
                      </div>
                      {(flag.candidates ?? []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2 pl-5">
                          {flag.candidates!.map(candidate => (
                            <Button
                              key={candidate.label}
                              variant="outline"
                              size="sm"
                              onClick={() => setMediumValues(prev => ({ ...prev, [field]: candidate.value }))}
                            >
                              {candidate.label} = {candidate.value}
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </li>
              );
            })}
            {Object.keys(partitioned.medium).length === 0 && (
              <li className="px-3 py-2 text-[11px] text-text-muted">No medium-confidence extractions.</li>
            )}
          </ul>
        </section>

        {/* Missing — manual entry. */}
        {missingFields.length > 0 && (
          <section className="border border-border rounded-md overflow-hidden">
            <header className="px-3 py-2 border-b border-border bg-state-hover/40">
              <span className="text-[11px] font-semibold uppercase tracking-widest text-text-muted">
                Missing — enter manually if needed
              </span>
            </header>
            <ul className="divide-y divide-border">
              {missingFields.map(field => (
                <li key={field} className="grid grid-cols-[220px_1fr_auto] gap-3 px-3 py-2 items-center">
                  <span className="text-[11px] text-text-secondary">{prettyFieldName(field)}</span>
                  <Input
                    type="text"
                    placeholder="Enter value"
                    value={missingValues[field] ?? ''}
                    onChange={e => setMissingValues(prev => ({ ...prev, [field]: e.target.value }))}
                  />
                  <span className="type-body uppercase tracking-widest text-text-muted inline-flex items-center gap-1">
                    <Edit3 className="w-3 h-3" /> manual
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {error && (
          <div className="text-[11px] text-danger flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> {error}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onDiscard} disabled={committing}>
            Discard upload
          </Button>
          <Button
            onClick={() => void handleCommit()}
            disabled={committing || commitBlockedReason != null}
            title={commitBlockedReason ?? undefined}
          >
            {committing ? 'Committing…' : 'Commit values'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────

/**
 * Partition by confidence tier — except that a field carrying a blocking
 * review flag is forced into the `medium` (per-row decision) bucket no
 * matter what tier found it. A high-confidence named range with an
 * implausible $/kW is exactly the value that must not auto-accept.
 */
function partitionByTier(
  extraction: ExtractionResult,
  blockingFields: Set<FinancialField> = new Set(),
): {
  high: Partial<Record<FinancialField, unknown>>;
  medium: Partial<Record<FinancialField, unknown>>;
  low: Partial<Record<FinancialField, unknown>>;
} {
  const high: Partial<Record<FinancialField, unknown>> = {};
  const medium: Partial<Record<FinancialField, unknown>> = {};
  const low: Partial<Record<FinancialField, unknown>> = {};
  for (const [field, value] of Object.entries(extraction.assumptions)) {
    const fp = extraction.fieldProvenance[field as FinancialField];
    if (!fp) continue;
    if (blockingFields.has(field as FinancialField)) {
      medium[field as FinancialField] = value;
      continue;
    }
    if (fp.confidence === 'high') high[field as FinancialField] = value;
    else if (fp.confidence === 'medium') medium[field as FinancialField] = value;
    else if (fp.confidence === 'low') low[field as FinancialField] = value;
  }
  return { high, medium, low };
}

function groupFlagsByField(flags: ExtractionReviewFlag[]): Map<FinancialField, ExtractionReviewFlag[]> {
  const map = new Map<FinancialField, ExtractionReviewFlag[]>();
  for (const flag of flags) {
    for (const field of flag.fields) {
      const list = map.get(field) ?? [];
      list.push(flag);
      map.set(field, list);
    }
  }
  return map;
}

function applyFieldOnto(target: FinancialAssumptions, field: FinancialField, value: unknown): void {
  // The FA core mixes typed-string (inServiceDate) and numeric fields.
  // We coerce via the same logic the form uses.
  if (field === 'inServiceDate') {
    (target as unknown as Record<string, unknown>)[field] = value == null ? null : String(value);
    return;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    (target as unknown as Record<string, unknown>)[field] = value;
    return;
  }
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/[$,\s%]/g, ''));
    if (Number.isFinite(parsed)) {
      (target as unknown as Record<string, unknown>)[field] = parsed;
    }
  }
}

function prettyFieldName(field: FinancialField): string {
  const LABELS: Partial<Record<FinancialField, string>> = {
    capacityMW: 'Capacity (MW)',
    capacityFactorPct: 'Capacity factor (%)',
    projectLifeYears: 'Project life (yr)',
    inServiceDate: 'In-service date',
    capexTotalUsd: 'Total CAPEX ($)',
    capexPerKwUsd: 'CAPEX / kW',
    constructionMonths: 'Construction (months)',
    decommissioningUsd: 'Decommissioning ($)',
    workingCapitalUsd: 'Working capital ($)',
    contingencyPct: 'Contingency (%)',
    fixedOmPerKwYearUsd: 'Fixed O&M ($/kW/yr)',
    variableOmPerMwhUsd: 'Variable O&M ($/MWh)',
    fuelCostPerMwhUsd: 'Fuel cost ($/MWh)',
    insuranceAndPropertyTaxPct: 'Insurance & property tax (%)',
    debtToEquityRatio: 'Debt fraction (0-1)',
    debtRatePct: 'Debt rate (%)',
    equityHurdlePct: 'Equity hurdle (%)',
    waccPct: 'WACC (%)',
    taxRatePct: 'Tax rate (%)',
    itcPct: 'ITC (%)',
    ptcPerMwhUsd: 'PTC ($/MWh)',
    ppaPricePerMwhUsd: 'PPA price ($/MWh)',
    ppaEscalatorPctPerYear: 'PPA escalator (%/yr)',
    ppaTermYears: 'PPA term (yr)',
    lcoeNominalPerMwhUsd: 'LCOE nominal ($/MWh)',
    lcoeRealPerMwhUsd: 'LCOE real ($/MWh)',
    npvUsd: 'NPV ($)',
    projectIrrPct: 'Project IRR (%)',
    equityIrrPct: 'Equity IRR (%)',
    paybackYears: 'Payback (yr)',
  };
  return LABELS[field] ?? field;
}

function prettyFormatValue(field: FinancialField, value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'number') {
    if (Math.abs(value) >= 1000) return Math.round(value).toLocaleString('en-US');
    return String(value);
  }
  return String(value);
}

// Note: this file uses `FinancialAssumptionsCore` only via the field
// keys. Re-export the import so tsc keeps the type binding alive when
// other parts of this module evolve.
export type { FinancialAssumptionsCore };
